/**
 * CR104+CR312+CR396: Add Source Modal
 * - Supports creating NEW merchants via combobox
 * - Enforces category for new merchants AND existing merchants without category
 * - Source label is derived from source type (not editable)
 * - CR312: Auto-prefills web_recipe_template_id from S0 default for WEB sources
 * - CR396: Fetches merchants from live directory (not stale summary), uses UUID linkage
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SourceTypeSelect } from "./SourceTypeSelect";
import { MerchantCombobox, type MerchantSelection, type MerchantOption } from "./MerchantCombobox";
import { type CanonicalSourceType, getSourceTypeLabel } from "@/lib/sourceTypes";
import { ALL_CATEGORIES, getCategoryLabel, type CategoryKey } from "@/lib/config/categories";
import { AlertTriangle, Tag } from "lucide-react";
import type { MerchantSource, SourceStatus } from "@/hooks/useMerchantSources";
import { useWebRecipeOptions } from "@/hooks/useWebRecipeOptions";
import { supabase } from "@/integrations/supabase/client";

interface AddSourceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (
    source: Omit<MerchantSource, "id" | "created_at" | "updated_at">,
    merchantCategory?: CategoryKey | null,
    merchantDisplayName?: string | null
  ) => Promise<any>;
}

/** Sentinel value for "no recipe / heuristic" */
const NONE_VALUE = "__none__";

export function AddSourceModal({
  open,
  onOpenChange,
  onSave,
}: AddSourceModalProps) {
  // CR396: Fetch merchants from live directory on modal open
  const [merchantOptions, setMerchantOptions] = useState<MerchantOption[]>([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setMerchantsLoading(true);
    
    // Fetch ALL merchants (including pending) so S6.5-created merchants appear
    supabase
      .from("merchants")
      .select("id, merchant_key, display_name, category")
      .order("display_name")
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error("[AddSourceModal] Failed to fetch merchants:", error);
          setMerchantsLoading(false);
          return;
        }
        setMerchantOptions(
          (data || []).map((m: any) => ({
            merchant_id: m.merchant_key,
            merchant_uuid: m.id,
            merchant_name: m.display_name,
            category: m.category,
          }))
        );
        setMerchantsLoading(false);
      });

    return () => { cancelled = true; };
  }, [open]);

  // Merchant selection state
  const [merchantSelection, setMerchantSelection] = useState<MerchantSelection | null>(null);
  const [merchantDisplayName, setMerchantDisplayName] = useState("");

  // Source fields
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceType, setSourceType] = useState<CanonicalSourceType>("gift_card_page");
  const [priority, setPriority] = useState(100);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Category state
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey | "">("");

  // CR312: Web recipe state
  const [selectedRecipeId, setSelectedRecipeId] = useState<string>(NONE_VALUE);
  const { templateRecipes, otherRecipes, s0DefaultRecipeId, isLoading: recipesLoading } =
    useWebRecipeOptions();

  // Derived: is this a WEB source?
  const isWebSource = sourceType !== "official_flyer";

  // CR396: Debug logging
  useEffect(() => {
    if (open) {
      console.log("[AddSourceModal] render", {
        version: "CR396-v1",
        merchantSelection,
        merchantOptionsCount: merchantOptions?.length,
      });
    }
  }, [open, merchantSelection, merchantOptions]);

  // CR312: When modal opens or source type changes to WEB, prefill recipe from S0 default
  useEffect(() => {
    if (open && isWebSource && !recipesLoading) {
      setSelectedRecipeId(s0DefaultRecipeId ?? NONE_VALUE);
    }
  }, [open, isWebSource, s0DefaultRecipeId, recipesLoading]);

  // Reset recipe when switching to FLYER
  useEffect(() => {
    if (!isWebSource) {
      setSelectedRecipeId(NONE_VALUE);
    }
  }, [isWebSource]);

  const resetForm = () => {
    setMerchantSelection(null);
    setMerchantDisplayName("");
    setSourceUrl("");
    setSourceType("gift_card_page");
    setPriority(100);
    setNotes("");
    setError(null);
    setSelectedCategory("");
    setSelectedRecipeId(NONE_VALUE);
  };

  // Derived state
  const isNewMerchant = merchantSelection?.isNew ?? false;
  const existingMerchantHasCategory = !isNewMerchant && merchantSelection?.existingCategory;
  const needsCategorySelection = isNewMerchant || (merchantSelection && !merchantSelection.existingCategory);

  // Update display name when selecting existing merchant
  useEffect(() => {
    if (merchantSelection) {
      setMerchantDisplayName(merchantSelection.merchant_name);
      if (merchantSelection.existingCategory) {
        setSelectedCategory(merchantSelection.existingCategory as CategoryKey);
      } else {
        setSelectedCategory("");
      }
    }
  }, [merchantSelection]);

  const handleSave = async () => {
    setError(null);

    // Validation
    if (!merchantSelection) {
      setError("Please select or create a merchant");
      return;
    }

    if (isNewMerchant && !merchantDisplayName.trim()) {
      setError("Merchant Display Name is required for new merchants");
      return;
    }

    if (!sourceUrl.trim()) {
      setError("Source URL is required");
      return;
    }

    try {
      new URL(sourceUrl);
    } catch {
      setError("Invalid URL format");
      return;
    }

    if (!sourceUrl.startsWith("https://")) {
      setError("URL must use HTTPS");
      return;
    }

    if (needsCategorySelection && !selectedCategory) {
      setError(
        "Category is required for " +
          (isNewMerchant ? "new merchants" : "this merchant (currently uncategorized)")
      );
      return;
    }

    setSaving(true);
    try {
      const scanType = sourceType === "official_flyer" ? "FLYER" : "WEB";

      // CR312: Resolve recipe ID — NONE means null (heuristic)
      const recipeId =
        isWebSource && selectedRecipeId !== NONE_VALUE ? selectedRecipeId : null;

      await onSave(
        {
          merchant_id: merchantSelection.merchant_id,
          merchant_uuid: merchantSelection.merchant_uuid || undefined, // CR396: pass UUID
          merchant_name: merchantDisplayName.trim() || merchantSelection.merchant_name,
          source_url: sourceUrl.trim(),
          source_label: getSourceTypeLabel(sourceType),
          source_type: sourceType,
          scan_type: scanType as "WEB" | "FLYER",
          priority,
          is_active: true,
          status: "active" as SourceStatus,
          notes: notes.trim() || null,
          web_recipe_template_id: recipeId,
        } as any,
        needsCategorySelection ? (selectedCategory as CategoryKey) : null,
        isNewMerchant ? merchantDisplayName.trim() : null
      );
      resetForm();
      onOpenChange(false);
    } catch (err: any) {
      const errorMessage = err?.message || err?.error_description || "Failed to save source";
      const errorCode = err?.code || err?.error?.code || "";
      const fullError = errorCode ? `[${errorCode}] ${errorMessage}` : errorMessage;
      console.error("[AddSourceModal] Save failed:", err);
      setError(fullError);
    } finally {
      setSaving(false);
    }
  };

  // Find the name of the S0 default recipe for helper text
  const s0DefaultName =
    s0DefaultRecipeId
      ? [...templateRecipes, ...otherRecipes].find((r) => r.id === s0DefaultRecipeId)?.recipe_name
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) resetForm();
        onOpenChange(newOpen);
      }}
    >
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Source</DialogTitle>
          <DialogDescription>
            Add a new source URL for an existing or new merchant. Sources are tried in priority order
            during scans.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Merchant Selection (Combobox) */}
          <div className="grid gap-2">
            <Label>Merchant *</Label>
            <MerchantCombobox
              merchants={merchantOptions}
              value={merchantSelection}
              onChange={setMerchantSelection}
            />
            {isNewMerchant && (
              <p className="text-xs text-primary flex items-center gap-1">
                <Tag className="h-3 w-3" />
                New merchant — will be created with this source
              </p>
            )}
            {merchantSelection && !isNewMerchant && (
              <p className="text-xs text-muted-foreground">
                Adding source to existing merchant: <strong>{merchantSelection.merchant_name}</strong>{" "}
                <span className="opacity-60">(Key: {merchantSelection.merchant_id})</span>
              </p>
            )}
          </div>

          {/* Display Name */}
          {merchantSelection && (
            <div className="grid gap-2">
              <Label htmlFor="merchant_name">
                Merchant Display Name{" "}
                {isNewMerchant && <span className="text-destructive">*</span>}
              </Label>
              {isNewMerchant ? (
                <Input
                  id="merchant_name"
                  placeholder="e.g. IKEA Canada"
                  value={merchantDisplayName}
                  onChange={(e) => setMerchantDisplayName(e.target.value)}
                />
              ) : (
                <div className="flex items-center h-10 px-3 rounded-md border bg-muted/50 text-sm">
                  {merchantDisplayName}
                </div>
              )}
            </div>
          )}

          {/* Category Section */}
          {merchantSelection && (
            <div className="grid gap-2">
              <Label>
                Merchant Category{" "}
                {needsCategorySelection && <span className="text-destructive">*</span>}
              </Label>

              {needsCategorySelection ? (
                <>
                  <Select
                    value={selectedCategory}
                    onValueChange={(val) => setSelectedCategory(val as CategoryKey)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {getCategoryLabel(cat)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {isNewMerchant
                      ? "Required for new merchants — used in frontend filters"
                      : "This merchant has no category — please assign one"}
                  </p>
                </>
              ) : existingMerchantHasCategory ? (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {getCategoryLabel(merchantSelection.existingCategory)}
                  </Badge>
                  <span className="text-xs text-muted-foreground">(from existing merchant)</span>
                </div>
              ) : null}
            </div>
          )}

          {/* Source URL */}
          <div className="grid gap-2">
            <Label htmlFor="source_url">Source URL *</Label>
            <Input
              id="source_url"
              type="url"
              placeholder="https://www.example.com/gift-cards"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
            />
          </div>

          {/* Source Type + Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Source Type *</Label>
              <SourceTypeSelect value={sourceType} onChange={setSourceType} />
              <p className="text-xs text-muted-foreground">
                Label: {getSourceTypeLabel(sourceType)}
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="priority">Priority</Label>
              <Input
                id="priority"
                type="number"
                min={1}
                max={1000}
                value={priority}
                onChange={(e) => setPriority(parseInt(e.target.value) || 100)}
              />
              <p className="text-xs text-muted-foreground">Lower = higher priority</p>
            </div>
          </div>

          {/* CR312: Web Recipe dropdown (only for WEB sources) */}
          {isWebSource && (
            <div className="grid gap-2">
              <Label>Web Recipe</Label>
              <Select
                value={selectedRecipeId}
                onValueChange={setSelectedRecipeId}
                disabled={recipesLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select recipe..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>None (heuristic crawl)</SelectItem>
                  {templateRecipes.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Templates
                      </SelectLabel>
                      {templateRecipes.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.recipe_name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {otherRecipes.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Other recipes
                      </SelectLabel>
                      {otherRecipes.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.recipe_name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {s0DefaultRecipeId && selectedRecipeId === s0DefaultRecipeId ? (
                  <>Using S0 default: <strong>{s0DefaultName}</strong></>
                ) : selectedRecipeId === NONE_VALUE ? (
                  "No recipe — Firecrawl will scrape with default settings."
                ) : (
                  "Source-level override — takes priority over S0 default."
                )}
              </p>
            </div>
          )}

          {/* Notes */}
          <div className="grid gap-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any notes about this source..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Add Source"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
